import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormFeedback,
    Button,
    Table,
    Input,
    Label,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import BreadCrumb from '../../../Components/Common/BreadCrumb'; // Adjust path as needed
import api, { handleApiError } from '../../../helpers/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isEmpty } from 'lodash';
import Loader from '../../../Components/Common/Loader'; // Adjust path as needed
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';

const ClientList = () => {
    const { t } = useTranslation();
    document.title = t('client_list.title');

    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [modal, setModal] = useState({ type: null, open: false, data: null });
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 10;

    // Fetch clients
    useEffect(() => {
        const fetchClients = async () => {
            setLoading(true);
            try {
                const response = await api.get('/user/client');
                const clientData = Array.isArray(response.data.clients) ? response.data.clients : [];
                setClients(clientData);
                setFilteredClients(clientData);
            } catch (error) {
                handleApiError(error, null, (path) => window.location.href = path);
                setError(t('client_list.errors.fetch_clients'));
            } finally {
                setLoading(false);
            }
        };
        fetchClients();
    }, [t]);

    // Handle search and filter
    useEffect(() => {
        let filtered = Array.isArray(clients) ? clients : [];
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (client) =>
                    client.firstname?.toLowerCase().includes(query) ||
                    client.lastname?.toLowerCase().includes(query) ||
                    client.phoneNumber?.includes(query) ||
                    client.email?.toLowerCase().includes(query)
            );
        }
        if (statusFilter !== 'All') {
            filtered = filtered.filter((client) => client.active === (statusFilter === 'Active'));
        }
        setFilteredClients(filtered);
        setCurrentPage(1); // Reset to first page on filter change
    }, [searchQuery, statusFilter, clients]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentClients = Array.isArray(filteredClients) ? filteredClients.slice(indexOfFirstItem, indexOfLastItem) : [];
    const totalPages = Array.isArray(filteredClients) && filteredClients.length > 0 ? Math.ceil(filteredClients.length / itemsPerPage) : 1;

    // Toggle modal
    const toggleModal = (type = null, data = null) => {
        setModal({ type, open: !!type, data });
        setError(null);
    };

    // Handle ban/unban client
    const handleBanClient = async (clientId, newStatus) => {
        try {
            await api.patch(`/user/${clientId}`, { active: newStatus });
            const updatedClients = clients.map((client) =>
                client._id === clientId ? { ...client, active: newStatus } : client
            );
            setClients(updatedClients);
            setFilteredClients(updatedClients);
            toast.success(t('client_list.success.ban_client', { action: newStatus ? t('client_list.actions.unban') : t('client_list.actions.ban') }));
            toggleModal();
        } catch (error) {
            const errorMessage = t('client_list.errors.ban_client', { action: newStatus ? t('client_list.actions.unban') : t('client_list.actions.ban') });
            setError(errorMessage);
            toast.error(errorMessage);
        }
    };

    // Handle delete client
    const handleDeleteClient = async (clientId) => {
        try {
            await api.delete(`/user/${clientId}`);
            const updatedClients = clients.filter((client) => client._id !== clientId);
            setClients(updatedClients);
            setFilteredClients(updatedClients);
            toast.success(t('client_list.success.delete_client'));
            toggleModal();
        } catch (error) {
            const errorMessage = t('client_list.errors.delete_client');
            setError(errorMessage);
            toast.error(errorMessage);
        }
    };

    // CSV export data
    const csvData = useMemo(
        () =>
            Array.isArray(filteredClients)
                ? filteredClients.map((client) => ({
                    FirstName: client.firstname || '',
                    LastName: client.lastname || '',
                    PhoneNumber: client.phoneNumber || '',
                    Email: client.email || '',
                    Status: client.active ? t('client_list.status.active') : t('client_list.status.banned'),
                }))
                : [],
        [filteredClients, t]
    );

    // Table columns
    const columns = useMemo(
        () => [
            {
                Header: t('client_list.table.first_name'),
                accessor: 'firstname',
            },
            {
                Header: t('client_list.table.last_name'),
                accessor: 'lastname',
            },
            {
                Header: t('client_list.table.phone_number'),
                accessor: 'phoneNumber',
            },
            {
                Header: t('client_list.table.email'),
                accessor: 'email',
            },
            {
                Header: t('client_list.table.status'),
                accessor: 'active',
                Cell: ({ row }) => {
                    const value = row.original.active;
                    return (
                        <span
                            className={`badge text-uppercase badge-soft-${value ? 'success' : 'danger'}`}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                {value ? t('client_list.status.active') : t('client_list.status.banned')}
              </span>
                    );
                }
            },
            {
                Header: t('client_list.table.action'),
                Cell: ({ row }) => (
                    <ul className="list-inline hstack gap-2 mb-0">
                        <li className="list-inline-item">
                            <Button
                                color={row.original.active ? 'warning' : 'success'}
                                size="sm"
                                onClick={() => toggleModal('banClient', row.original)}
                                style={{
                                    padding: '5px 10px',
                                    fontSize: '12px',
                                    borderRadius: '4px',
                                }}
                            >
                                <i className={row.original.active ? 'ri-forbid-line' : 'ri-check-line'}></i>
                                {row.original.active ? t('client_list.actions.ban') : t('client_list.actions.unban')}
                            </Button>
                        </li>
                        <li className="list-inline-item">
                            <Button
                                color="danger"
                                size="sm"
                                onClick={() => toggleModal('deleteClient', row.original)}
                                style={{ padding: '5px 10px', fontSize: '12px', borderRadius: '4px' }}
                            >
                                <i className="ri-delete-bin-line"></i> {t('client_list.actions.delete')}
                            </Button>
                        </li>
                        <li className="list-inline-item">
                            <Link
                                to={`/apps-ecommerce-orders?client=${row.original._id}`}
                                className="btn btn-primary btn-sm"
                                style={{ padding: '5px 10px', fontSize: '12px', borderRadius: '4px' }}
                            >
                                <i className="ri-eye-line"></i> {t('client_list.actions.view_orders')}
                            </Link>
                        </li>
                    </ul>
                ),
            },
        ],
        [t]
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={t('client_list.title')} pageTitle={t('client_list.page_title')} />
                <Row>
                    <Col lg={12}>
                        <Card style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                            <CardHeader style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', padding: '15px' }}>
                                <Row className="g-4 align-items-center">
                                    <Col sm>
                                        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>{t('client_list.title')}</h5>
                                    </Col>
                                    <Col sm="auto">
                                        <div className="hstack gap-2">
                                            <CSVLink
                                                data={csvData}
                                                filename={'clients.csv'}
                                                className="btn btn-info"
                                                style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', textDecoration: 'none' }}
                                            >
                                                <i className="ri-file-download-line me-1"></i> {t('client_list.actions.export_csv')}
                                            </CSVLink>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <div className="card-body pt-0" style={{ padding: '20px' }}>
                                <Row className="g-2 mb-3 align-items-end">
                                    <Col xl={3}>
                                        <div style={{ position: 'relative' }}>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                placeholder={t('client_list.search_placeholder')}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                style={{
                                                    paddingRight: '30px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    fontSize: '14px',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                    transition: 'border-color 0.2s ease',
                                                }}
                                                onFocus={(e) => (e.target.style.borderColor = '#405189')}
                                                onBlur={(e) => (e.target.style.borderColor = '#ced4da')}
                                            />
                                            <i
                                                className="ri-search-line"
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: '10px',
                                                    transform: 'translateY(-50%)',
                                                    color: '#6c757d',
                                                    fontSize: '16px',
                                                }}
                                            ></i>
                                        </div>
                                    </Col>
                                    <Col xl={2}>
                                        <div>
                                            <Label
                                                htmlFor="status-filter"
                                                style={{
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    color: '#495057',
                                                    marginBottom: '5px',
                                                    display: 'block',
                                                }}
                                            >
                                                {t('client_list.form.status_filter')}
                                            </Label>
                                            <Input
                                                type="select"
                                                id="status-filter"
                                                className="form-control"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                style={{
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    fontSize: '14px',
                                                    padding: '8px',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                                    backgroundColor: '#fff',
                                                    cursor: 'pointer',
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#405189';
                                                    e.target.style.boxShadow = '0 0 5px rgba(64,81,137,0.3)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#ced4da';
                                                    e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                                                }}
                                            >
                                                <option value="All">{t('client_list.status.all')}</option>
                                                <option value="Active">{t('client_list.status.active')}</option>
                                                <option value="Banned">{t('client_list.status.banned')}</option>
                                            </Input>
                                        </div>
                                    </Col>
                                </Row>
                                {loading ? (
                                    <Loader />
                                ) : isEmpty(clients) ? (
                                    <p style={{ fontSize: '14px', textAlign: 'center', marginTop: '20px' }}>{t('client_list.no_clients')}</p>
                                ) : filteredClients.length > 0 ? (
                                    <>
                                        <Table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                                {columns.map((column) => (
                                                    <th
                                                        key={column.Header}
                                                        style={{ padding: '10px', fontSize: '14px', fontWeight: '500', textAlign: 'left' }}
                                                    >
                                                        {column.Header}
                                                    </th>
                                                ))}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {currentClients.map((client) => (
                                                <tr key={client._id} style={{ borderBottom: '1px solid #e9ecef' }}>
                                                    {columns.map((column) => (
                                                        <td key={column.Header} style={{ padding: '10px', fontSize: '14px' }}>
                                                            {column.Cell
                                                                ? column.Cell({ row: { original: client } })
                                                                : client[column.accessor] || ''}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                        <Row className="mt-3">
                                            <Col sm={6}>
                                               <p style={{ margin: 0, fontSize: '14px' }}>
                                                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredClients.length)} of {filteredClients.length}
                                                </p>
                                            </Col>
                                            <Col sm={6} className="text-sm-end">
                                                <ul style={{ listStyle: 'none', display: 'flex', justifyContent: 'flex-end', gap: '5px', margin: 0 }}>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                                        <li key={page}>
                                                            <Button
                                                                color={currentPage === page ? 'primary' : 'light'}
                                                                onClick={() => setCurrentPage(page)}
                                                                style={{
                                                                    padding: '5px 10px',
                                                                    margin: '0 2px',
                                                                    border: '1px solid #ced4da',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: currentPage === page ? '#405189' : '#fff',
                                                                    color: currentPage === page ? '#fff' : '#000',
                                                                    fontSize: '14px',
                                                                }}
                                                            >
                                                                {page}
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    <p style={{ fontSize: '14px', textAlign: 'center', marginTop: '20px' }}>{t('client_list.no_clients_filtered')}</p>
                                )}
                                {error && (
                                    <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '15px', textAlign: 'center' }}>{error}</div>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Ban/Unban Client Modal */}
                <Modal isOpen={modal.type === 'banClient'} toggle={() => toggleModal()}>
                    <ModalHeader
                        toggle={() => toggleModal()}
                        style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', padding: '15px' }}
                    >
                        {modal.data?.active ? t('client_list.modal.ban_client') : t('client_list.modal.unban_client')}
                    </ModalHeader>
                    <ModalBody style={{ padding: '20px' }}>
                        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                            {t('client_list.modal.ban_unban_message', {
                                action: modal.data?.active ? t('client_list.actions.ban') : t('client_list.actions.unban'),
                                firstName: modal.data?.firstname,
                                lastName: modal.data?.lastname
                            })}
                        </p>
                        {error && <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545' }}>{error}</FormFeedback>}
                    </ModalBody>
                    <ModalFooter style={{ borderTop: '1px solid #e9ecef', padding: '15px' }}>
                        <Button
                            color="light"
                            onClick={() => toggleModal()}
                            style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        >
                            {t('client_list.actions.cancel')}
                        </Button>
                        <Button
                            color={modal.data?.active ? 'warning' : 'success'}
                            onClick={() => handleBanClient(modal.data?._id, !modal.data?.active)}
                            style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                        >
                            {modal.data?.active ? t('client_list.actions.ban') : t('client_list.actions.unban')}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Delete Client Modal */}
                <Modal isOpen={modal.type === 'deleteClient'} toggle={() => toggleModal()}>
                    <ModalHeader
                        toggle={() => toggleModal()}
                        style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', padding: '15px' }}
                    >
                        {t('client_list.modal.delete_client')}
                    </ModalHeader>
                    <ModalBody style={{ padding: '20px' }}>
                        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                            {t('client_list.modal.delete_client_message', {
                                firstName: modal.data?.firstname,
                                lastName: modal.data?.lastname
                            })}
                        </p>
                        {error && <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545' }}>{error}</FormFeedback>}
                    </ModalBody>
                    <ModalFooter style={{ borderTop: '1px solid #e9ecef', padding: '15px' }}>
                        <Button
                            color="light"
                            onClick={() => toggleModal()}
                            style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        >
                            {t('client_list.actions.cancel')}
                        </Button>
                        <Button
                            color="danger"
                            onClick={() => handleDeleteClient(modal.data?._id)}
                            style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                        >
                            {t('client_list.actions.delete')}
                        </Button>
                    </ModalFooter>
                </Modal>

                <ToastContainer closeButton={false} limit={1} />
            </Container>
        </div>
    );
};

export default ClientList;